/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
//@ts-nocheck
import React from 'react';
import { Carousel, Image } from 'react-bootstrap';
import { ScrollMenu, VisibilityContext } from 'react-horizontal-scrolling-menu';
import { useBreakpoint } from '@vlinder-web/breakpoint-hook-widget-react';
import { MapWidget } from '@vlinder-web/map-widget-react';
import { LeftArrow, RightArrow } from './arrows';
import { BlurBackground, getImageStyle } from '../BlurBackground';
import { PdfViewer } from './pdfViewer';
import { ImageOverlay } from './components/ImageOverlay';
import QRCode from 'react-qr-code';

import './styles.css';

import * as S from './styles';

const DEFAULT_TITLE = '';

export interface ICarouselItem {
  type: 'img' | 'pdf' | 'video' | 'map' | 'qr';
  poster?: string;
  src: string;
  title?: string;
  accessToken?: string;
  markers?: any[];
  center?: number[];
}

export interface ICarouselSectionProps {
  carouselItems?: ICarouselItem[];
  onCarouselImageClick?(item?: ICarouselItem): void;
  carouselStyles: {
    width: string;
    height: string;
  };
}

const DEFAULT_PDF_POSTER =
  'https://s3.ap-south-1.amazonaws.com/io.vlinder.illustrations/pdf-illustration.png';
const DEFAULT_PLAY_OVERLAY_ICON =
  'https://s3.ap-south-1.amazonaws.com/io.vlinder.icons/play-button.png';
const DEFAULT_PDF_OVERLAY_ICON =
  'https://s3.ap-south-1.amazonaws.com/io.vlinder.icons/pdf.png';
const DEFAULT_VIDEO_POSTER = 'https://i.ibb.co/N3R8JRk/video-camera-png.webp';
const DEFAULT_MAP_POSTER =
  'https://s3.ap-south-1.amazonaws.com/io.vlinder.icons/pdf.png';

function onWheel(apiObj: scrollVisibilityApiType, ev: React.WheelEvent): void {
  const isThouchpad = Math.abs(ev.deltaX) !== 0 || Math.abs(ev.deltaY) < 15;

  if (isThouchpad) {
    ev.stopPropagation();
    return;
  }

  if (ev.deltaY < 0) {
    apiObj.scrollNext();
  } else if (ev.deltaY > 0) {
    apiObj.scrollPrev();
  }
}

export const CarouselSection = ({
  carouselItems,
  carouselStyles,
  onCarouselImageClick,
}: ICarouselSectionProps) => {
  const breakpoint = useBreakpoint();

  const _getImageWidth = () => {
    let _style: any = {
      width: carouselStyles?.width ?? '350px',
    };
    if (breakpoint === 'xs' || breakpoint === 'sm') {
      _style.width = '300px';
    }
    return _style;
  };

  const _getImageHeight = () => {
    let _style: any = {
      height: carouselStyles?.height ?? '200px',
    };
    if (breakpoint === 'xs' || breakpoint === 'sm') {
      _style.height = '200px';
    }
    return _style;
  };

  const _renderType = (item: ICarouselItem, index: number) => {
    let _comp: any = [];
    if (item?.type === 'img' && item?.src) {
      _comp.push(
        <BlurBackground
          backgroundImage={item.src}
          width={_getImageWidth().width}
          height={_getImageHeight().height}
          onClick={onCarouselImageClick?.bind(null, item)}
        >
          <Image
            key={`prdv3-carousel-item-img-${index}`}
            src={item.src}
            style={{
              ...getImageStyle(),
              objectFit: 'cover',
              height: _getImageHeight().height,
              padding: 0,
            }}
            onClick={onCarouselImageClick?.bind(null, item)}
          />
          {item?.title ?? DEFAULT_TITLE}
        </BlurBackground>
      );
    }
    if (item?.type === 'video' && item?.src) {
      const poster = item?.poster ?? DEFAULT_VIDEO_POSTER;
      _comp.push(
        <BlurBackground
          backgroundImage={poster}
          width={_getImageWidth().width}
          height={_getImageHeight().height}
          onClick={onCarouselImageClick?.bind(null, item)}
        >
          <ImageOverlay
            key={`prdv3-carousel-item-img-${index}`}
            src={poster}
            overlaySrc={DEFAULT_PLAY_OVERLAY_ICON}
            style={getImageStyle()}
            onClick={onCarouselImageClick?.bind(null, item)}
            imageStyle={{
              objectFit: 'cover',
              height: _getImageHeight().height,
              padding: 0,
            }}
          />
        </BlurBackground>
      );
    }
    if (item?.type === 'pdf') {
      const poster = item?.poster ?? DEFAULT_PDF_POSTER;
      _comp.push(
        <BlurBackground
          backgroundImage={poster}
          width={_getImageWidth().width}
          height={_getImageHeight().height}
          onClick={onCarouselImageClick?.bind(null, item)}
        >
          <PdfViewer file={item?.src} />
          <ImageOverlay
            key={`prdv3-carousel-item-img-${index}`}
            src={poster}
            overlaySrc={DEFAULT_PDF_OVERLAY_ICON}
            style={getImageStyle()}
            onClick={onCarouselImageClick?.bind(null, item)}
            imageStyle={{
              objectFit: 'cover',
              height: _getImageHeight().height,
              padding: 0,
            }}
          />
        </BlurBackground>
      );
    }
    if (item?.type === 'map' && item?.accessToken && item?.markers) {
      const poster = item?.poster ?? DEFAULT_MAP_POSTER;
      _comp.push(
        <BlurBackground
          backgroundImage={poster}
          width={_getImageWidth().width}
          height={_getImageHeight().height}
          onClick={onCarouselImageClick?.bind(null, item)}
        >
          <div style={{ padding: '0px' }}>
            <MapWidget
              accessToken={item.accessToken}
              markers={item.markers}
              center={item.center}
            />
          </div>
        </BlurBackground>
      );
    }
    if (item?.type === 'qr') {
      _comp.push(
        <BlurBackground
          // backgroundImage={item.src}
          width={_getImageWidth().width}
          height={_getImageHeight().height}
          onClick={onCarouselImageClick?.bind(null, item)}
        >
          <Image
            key={`prdv3-carousel-item-img-${index}`}
            src={item.poster}
            style={{
              ...getImageStyle(),
              objectFit: 'cover',
              height: _getImageHeight().height,
              padding: 0,
            }}
            onClick={onCarouselImageClick?.bind(null, item)}
          />
          {/* <QRCode
                        size={120}
                        style={{
                            height: "auto",
                            maxWidth: "100%",
                            width: "100%"
                        }}
                        value={item?.src}
                        viewBox={`0 0 120 120`}
                        onClick={onCarouselImageClick?.bind(null, item)}
                    /> */}
          {item?.title ?? DEFAULT_TITLE}
        </BlurBackground>
      );
    }
    return _comp;
  };

  return (
    <ScrollMenu LeftArrow={LeftArrow} RightArrow={RightArrow} onWheel={onWheel}>
      {carouselItems?.map((item, index) => (
        <div
          key={`pdv3-carousel-item-${index}`}
          style={S.CarouselItemContainer}
        >
          {_renderType(item, index)}
        </div>
      ))}
    </ScrollMenu>
  );
};

CarouselSection.defaultProps = {
  carouselItems: [],
  carouselStyles: {},
};
